import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { QuotationStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
import { BonusQtyStringSchema, SalesHeaderInputFields, SalesHeaderPatchFields, SalesHeaderResponseFields, TwoWayAmountSchema, CommissionInputFields, CommissionPatchFields, CommissionResponseFields, } from './sales-invoices.js';
const c = initContract();
/**
 * Quotations (PART 6, SAL-01).
 *
 * Permission area: `sales` (PART 16.2.2 row 1), per action:
 *   list/get = sales.view · create = sales.create · edit draft, status
 *   transitions and NEW REVISION = sales.edit (BR-SAL-01-16: the revision
 *   action is gated on one permission and nothing else) · delete draft =
 *   sales.delete · convert = sales.create (it creates a new draft document).
 *   Nothing here uses sales.approve: a quotation never posts (BR-SAL-01-11),
 *   so there is nothing to approve — EXCEPT when the company switches
 *   `invoicing.quote_internal_approval` on, which makes SEND itself require
 *   sales.approve (SAL-01 §8a's internal-approval feature, PART 15).
 *
 * Lifecycle (SAL-01 §7, the six chip-defined statuses — BR-SAL-01-01 removes
 * the two chipless ones): draft → sent → accepted / rejected / expired;
 * accepted or expired → converted. Expiry is a STORED transition, swept when
 * the quotation is next read or written (BR-SAL-01-09), and it NEVER blocks
 * acceptance or conversion (owner ruling R4, BR-SAL-01-14).
 *
 * Revisioning (SAL-01 §9): a quotation that has reached Sent is never edited
 * in place (BR-SAL-01-10a). NEW REVISION freezes the current document into an
 * immutable revision row, bumps the revision number, and returns the working
 * status to Draft (BR-SAL-01-10). Older versions stay readable forever.
 */
// ── The fixed sentences of SAL-01 ───────────────────────────────────────────
export const QUOTATION_MESSAGES = {
    /** BR-SAL-01-03 / ERR-SAL-01-01. */
    noLines: 'Add at least one item before converting this quotation.',
    /** BR-SAL-01-04 / ERR-SAL-01-02. */
    validUntilBeforeIssue: "The 'Valid until' date must be on or after the date issued.",
    /** BR-SAL-01-02. */
    rejectedResend: 'A rejected quotation cannot be sent again. Open a new revision.',
    /** BR-SAL-01-02's guard for every other illegal jump. */
    badTransition: (from, to) => `A quotation cannot move from ${from} to ${to}.`,
    /**
     * SAL-01 §8a — the `invoicing.quote_internal_approval` company switch:
     * when ON, sending is an approval act (sales.approve).
     */
    internalApprovalRequired: 'This company requires quotations to be approved before sending. Ask somebody with sales approval.',
    /**
     * ERR-SAL-01-03's sentence, the shape the spec prints ("Discount of 15.0%
     * exceeds the 10% threshold — manager approval required before sending"),
     * raised by the SEND gate (BR-SAL-01-06 in quotations.service.ts). The
     * percentage is the EFFECTIVE total discount, lines included, and the
     * threshold is the tenant's `invoicing.max_discount_pct` — both are SAL-01
     * §16's proposed defaults (question 2 reads the threshold "from the
     * settings hub, so the two numbers stop disagreeing"). Parameterised, so it
     * passes through the web's exact-match map in English (the documented
     * policy); the editor draws its own localized copy of the same sentence as
     * the live amber banner while the document is being written.
     */
    discountNeedsApproval: (pct, threshold) => `Discount of ${pct}% exceeds the ${threshold}% threshold — manager approval required before sending.`,
    /**
     * ERR-SAL-01-07, word for word — "clicked Accept quote with an empty pad".
     * Raised by the acceptance act while the tenant's
     * `invoicing.quote_customer_signature` switch is on and no signature came
     * with the request (BR-SAL-01-13's gate in quotations.service.ts). Fixed,
     * so the web's exact-match map localizes it (quotes.errSignFirst).
     */
    signBeforeAccept: 'Ask the customer to sign before accepting.',
    /**
     * BR-SAL-01-15's write-side guard: the Sales rep field takes only somebody
     * the picker itself would offer — an active employee whose ACTIVE job role
     * unlocks the `sales_rep` surface (BR-HR-01-05d's feed, checked with the
     * same predicate at the write). Fixed, so the web's exact-match map
     * localizes it (quotes.errSalesRepNotOffered).
     */
    salesRepNotOffered: 'Only a person with a sales-rep job role can be picked as the sales rep.',
    /** BR-SAL-01-10a — the in-place edit refusal. */
    sentLocked: 'This quotation has been sent. Open a new revision to change it.',
    /** SAL-01 §7: Converted = "Change anything: cannot". */
    convertedLocked: 'This quotation was converted. It can no longer change.',
    /**
     * SAL-01 §9 the other way round: a revision FREEZES an offer that went out
     * and reopens it; a draft never went out, is edited in place
     * (BR-SAL-01-10a), and so has nothing to freeze — a revision opened from a
     * draft would only file an identical copy as "prior version". Fixed, so
     * the web's exact-match map localizes it (quotes.errReviseFromDraft).
     */
    reviseFromDraft: 'This quotation is still a draft — edit it directly. A new revision is opened once it has been sent.',
    /** Conversion needs the customer's answer (or expiry + R4) first. */
    notConvertible: 'Only an accepted or expired quotation can be converted.',
    /** One conversion per quotation; the pill then links to the document. */
    alreadyConverted: 'This quotation was already converted.',
    /** BR-SAL-01-10's required note, word for word. */
    revisionNote: 'New revision opened for editing (prior version kept read-only)',
    /** R8-family: only drafts are deletable; sent+ is the customer's record. */
    notDraftDelete: 'Only a draft quotation can be deleted.',
    /** BR-SAL-01-10: frozen history must stay readable — the head cannot go. */
    historyNoDelete: 'A quotation with revision history cannot be deleted.',
};
/**
 * D46 — SAL-01's two parameterised quotation refusals, structured.
 * `badTransition`'s `from`/`to` are raw status TOKENS (the QuotationStatus
 * values); en interpolates them verbatim (the twin is bare, no phrasing), ar
 * selects them into Arabic, so the raw token is absent from the Arabic render.
 * `discountNeedsApproval` carries two PRE-FORMATTED percentage strings and
 * reuses `quotes.discountOverThreshold` — the key the editor's live amber
 * banner already names with the same two params. Sentence parity pinned by
 * `refusal-census.spec.ts`.
 */
export const QUOTATION_REFUSALS = {
    badTransition: (from, to) => ({
        message: QUOTATION_MESSAGES.badTransition(from, to),
        key: 'quotes.errBadTransition',
        params: { from, to },
    }),
    discountNeedsApproval: (pct, threshold) => ({
        message: QUOTATION_MESSAGES.discountNeedsApproval(pct, threshold),
        key: 'quotes.discountOverThreshold',
        params: { pct, threshold },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * One grid line (SAL-01 §5c). itemId is a real MD-03 reference when the line
 * was picked from the catalog; description is the fallback (and stays
 * editable either way). Qty is in the item's sell unit.
 */
export const QuotationLineInputSchema = z
    .object({
    itemId: z.uuid().optional(),
    /** INV-L3 on the quotation grid (QUO-01): the line's warehouse; absent = the header's. */
    warehouseId: z.uuid().optional(),
    /** INV-L4: the unit the qty was typed in; absent = the item's stock unit. */
    unitId: z.uuid().optional(),
    description: z.string().trim().min(1).max(200),
    qty: QtyStringSchema,
    /** INV-L1: the free quantity given with the line; omitted = 0. */
    bonusQty: BonusQtyStringSchema.optional(),
    unitPrice: MoneyStringSchema,
    discountPct: PctStringSchema.optional(),
})
    .strict();
export const QuotationLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    itemId: z.uuid().nullable(),
    /** ITM code when item-linked, for the grid's Code column (L2). */
    itemCode: z.string().nullable(),
    warehouseId: z.uuid().nullable(),
    unitId: z.uuid().nullable(),
    /** Wave 4C: the unit `qty` is in — the typed unit, else the item's stock unit; both null on a free-text line. */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    description: z.string(),
    qty: z.string(),
    bonusQty: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
})
    .strict();
export const QuotationStatusSchema = z.enum([
    'draft',
    'sent',
    'accepted',
    'rejected',
    'expired',
    'converted',
]);
/** A frozen prior version (SAL-01 §9's revisions table row). */
export const QuotationRevisionSchema = z
    .object({
    revisionNo: z.number().int().positive(),
    /** The frozen document: header + lines + totals at freeze time. */
    snapshot: z.object({
        status: QuotationStatusSchema,
        quotationDate: IsoDateSchema,
        validUntil: IsoDateSchema,
        reference: z.string().nullable(),
        terms: z.string().nullable(),
        /**
         * The rate the SUPERSEDED revision was sent at, and its "as of" date.
         * A customer holding revision 1 is entitled to be shown what revision 1
         * was worth, and the working document's own columns are cleared when it
         * reopens as a draft — so if this snapshot did not carry them, the
         * figure would be gone (EC-19-10: a rate change must never restate a
         * document that has already gone out). Null on a revision snapshotted
         * before this wave, and on one that never left Draft.
         */
        exchangeRate: z.string().nullable().optional(),
        rateEffectiveOn: IsoDateSchema.nullable().optional(),
        /**
         * The signature the SUPERSEDED revision was accepted with, if any —
         * the reopen clears the working column (a revision is a new,
         * unsigned offer), so without this the record BR-SAL-01-13 demands
         * would vanish on revision. Absent on snapshots frozen before the
         * signature wave.
         */
        acceptanceSignature: z.string().nullable().optional(),
        lines: QuotationLineSchema.array(),
        subtotal: z.string(),
        discountPct: z.string(),
        discountAmount: z.string(),
        taxPct: z.string(),
        taxAmount: z.string(),
        grandTotal: z.string(),
    }),
    /** BR-SAL-01-10's note, word for word. */
    note: z.string(),
    createdAt: z.string(),
})
    .strict();
export const QuotationSchema = z
    .object({
    id: z.uuid(),
    /** QUO-#### (§6.8 settled by the spec's proposed default). */
    code: z.string(),
    quotationNo: z.number().int().positive(),
    /** The CURRENT revision; the chip reads "v2 (Current)". */
    revisionNo: z.number().int().positive(),
    status: QuotationStatusSchema,
    quotationDate: IsoDateSchema,
    validUntil: IsoDateSchema,
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    /** Field 12 (BR-SAL-01-15): the employee the picker named, if any. */
    salesRepEmployeeId: z.uuid().nullable(),
    /** Joined for display — the id is the record, the name is the label. */
    salesRepName: z.string().nullable(),
    /** INV-F1…F6 — the header the three sales documents share (QUO-01). */
    ...SalesHeaderResponseFields,
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    /** Wave 4D — the sales commission (sales-invoices.ts). */
    ...CommissionResponseFields,
    reference: z.string().nullable(),
    terms: z.string().nullable(),
    /** Wave 4B: whom this offer is addressed to ("Attn:"), printed on the PDF. */
    attention: z.string().nullable(),
    lines: QuotationLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    grandTotal: z.string(),
    notes: z.string().nullable(),
    /**
     * BR-SAL-01-13: the stored customer signature (PNG data URI), written by
     * the acceptance act; null until a signed acceptance. The editor renders
     * it read-only — the pad is for capturing, this is the record.
     */
    signature: z.string().nullable(),
    /** The frozen history, newest first (SAL-01 §9). */
    revisions: QuotationRevisionSchema.array(),
    /** Set on Convert to Invoice; the strip pill links here (BR-SAL-01-12). */
    convertedInvoiceId: z.uuid().nullable(),
    convertedInvoiceCode: z.string().nullable(),
    /** Derived from sales_orders.source_quotation_id on Convert to Order. */
    convertedOrderId: z.uuid().nullable(),
    convertedOrderCode: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateQuotationRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionInputFields,
    partnerId: z.uuid(),
    quotationDate: IsoDateSchema,
    /** BR-SAL-01-04: on or after the issue date (server-checked). */
    validUntil: IsoDateSchema,
    /**
     * Field 12 — Sales rep. BR-SAL-01-15: only somebody the picker offers
     * (the `sales_rep` surface feed), server-checked with the same
     * predicate. Optional: the field is not yet required at the write —
     * §5b marks it required in the prototype's layout, but making it
     * blocking is a separate call recorded in the wave report.
     */
    salesRepEmployeeId: z.uuid().optional(),
    ...SalesHeaderInputFields,
    reference: z.string().trim().max(50).optional(),
    terms: z.string().max(2000).optional(),
    /** Wave 4B: the attention line beside the reference. */
    attention: z.string().trim().max(120).optional(),
    lines: QuotationLineInputSchema.array().min(1),
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).optional(),
})
    .strict();
export const UpdateQuotationRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    /** Wave 4D — the sales commission % (sales-invoices.ts). */
    ...CommissionPatchFields,
    quotationDate: IsoDateSchema.optional(),
    validUntil: IsoDateSchema.optional(),
    /** Field 12 (BR-SAL-01-15); null clears the assignment. */
    salesRepEmployeeId: z.uuid().nullable().optional(),
    ...SalesHeaderPatchFields,
    reference: z.string().trim().max(50).nullable().optional(),
    terms: z.string().max(2000).nullable().optional(),
    attention: z.string().trim().max(120).nullable().optional(),
    /** The grid saves as a set: present = replace all lines (draft only). */
    lines: QuotationLineInputSchema.array().min(1).optional(),
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
/**
 * W1 — the user-settable transitions. 'expired' is the system's alone
 * (BR-SAL-01-09), 'converted' belongs to the convert actions, 'draft' to New
 * revision; none of the three is settable here.
 */
export const SetQuotationStatusRequestSchema = z
    .object({
    status: z.enum(['sent', 'accepted', 'rejected']),
    /**
     * The acceptance card's pad (SAL-01 §5 A1/A5), as a PNG data URI —
     * meaningful only on `status: 'accepted'`. With the tenant's
     * `invoicing.quote_customer_signature` switch ON, accepting without it
     * is refused with ERR-SAL-01-07's sentence; when it arrives it is STORED
     * with the quotation (BR-SAL-01-13), never merely drawn. The bound
     * matches the column's CHECK (20260904100000).
     */
    signature: z
        .string()
        .regex(/^data:image\//)
        .max(200000)
        .optional(),
})
    .strict();
const quotationParams = z.object({ quotationId: z.uuid() }).strict();
export const quotationsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/sales/quotations',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            status: QuotationStatusSchema.optional(),
            /**
             * The list's search box (and the header rail's Find): the printed
             * number, the customer's name through `ar_norm`, and field 13's
             * customer Reference / RFQ number — the three things a row shows.
             */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the quotation date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('quotations', QuotationSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Quotations (sales.view), PAGED (NFR-42). Reading SWEEPS stored expiry first: sent past valid_until becomes expired (BR-SAL-01-09), so the sweep runs BEFORE the filter and the count.',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the quotations on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/sales/quotations/:quotationId/neighbours',
        pathParams: quotationParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next quotation by number (sales.view).',
    },
    /** S4 — the "Go to no." box: one quotation by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/sales/quotations/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One quotation by its printed number (sales.view).',
    },
    get: {
        method: 'GET',
        path: '/api/sales/quotations/:quotationId',
        pathParams: quotationParams,
        responses: {
            200: QuotationSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One quotation with its lines and frozen revision history (sales.view).',
    },
    create: {
        method: 'POST',
        path: '/api/sales/quotations',
        body: CreateQuotationRequestSchema,
        responses: {
            201: QuotationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft v1 (sales.create). Totals per PART 18 F-1..F-7; valid-until on or after the issue date (BR-SAL-01-04).',
    },
    update: {
        method: 'PATCH',
        path: '/api/sales/quotations/:quotationId',
        pathParams: quotationParams,
        body: UpdateQuotationRequestSchema,
        responses: {
            200: QuotationSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (sales.edit). A sent quotation answers 409 — "never edited in place, open a revision" (BR-SAL-01-10a).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/sales/quotations/:quotationId',
        pathParams: quotationParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (sales.delete); anything sent+ answers 409.',
    },
    setStatus: {
        method: 'POST',
        path: '/api/sales/quotations/:quotationId/status',
        pathParams: quotationParams,
        body: SetQuotationStatusRequestSchema,
        responses: {
            200: QuotationSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Guarded W1 transitions (sales.edit): draft→sent, sent→accepted/rejected, expired→accepted (R4). Rejected only leaves through a new revision (BR-SAL-01-02).',
    },
    newRevision: {
        method: 'POST',
        path: '/api/sales/quotations/:quotationId/revisions',
        pathParams: quotationParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: QuotationSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'NEW REVISION (sales.edit — BR-SAL-01-16): freezes the current document as an immutable revision row, bumps the revision number, returns the status to Draft (BR-SAL-01-10). Refused on a converted quotation and on a draft (nothing went out, so there is nothing to freeze — edit it in place, BR-SAL-01-10a).',
    },
    convertToInvoice: {
        method: 'POST',
        path: '/api/sales/quotations/:quotationId/convert-to-invoice',
        pathParams: quotationParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: z.object({ quotation: QuotationSchema, invoiceId: z.uuid() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Convert to Invoice (sales.create): creates a DRAFT sales invoice carrying every line, price and discount, records the quotation on it (BR-SAL-01-12), sets status converted. Allowed from accepted AND expired — expiry never blocks conversion (R4, BR-SAL-01-09).',
    },
    convertToOrder: {
        method: 'POST',
        path: '/api/sales/quotations/:quotationId/convert-to-order',
        pathParams: quotationParams,
        body: z.object({}).strict().optional(),
        responses: {
            201: z.object({ quotation: QuotationSchema, orderId: z.uuid() }).strict(),
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Convert to Order (sales.create): creates a DRAFT sales order carrying every line (SAL-02 "From QUO-####"), sets status converted. Same R4 rule: expiry never blocks.',
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/sales/quotations/stats',
        responses: {
            200: QuotationStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'SAL-01 list tiles (sales.view): open (sent) count and value, win rate over 90 days, expiring within 7 days — base currency, in SQL, after the expiry sweep.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=quotations.js.map